import { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom'
import { AppLayout } from './components/layout/AppLayout'
import { PropertyGuard } from './components/PropertyGuard'
import { PropertyLanding } from './components/PropertyLanding'
import { usePropertyStore } from './store/propertyStore'
import { ProtectedRoute } from './components/ProtectedRoute'
import { ErrorBoundary } from './components/ErrorBoundary'
import { LoginPage } from './features/auth/components/LoginPage'
import { ForgotPasswordPage } from './features/auth/components/ForgotPasswordPage'
import { ResetPasswordPage } from './features/auth/components/ResetPasswordPage'
import { ChangePasswordPage } from './features/auth/components/ChangePasswordPage'
import { IncidentsPage } from './pages/IncidentsPage'
import { LiveOpsPage } from './pages/LiveOpsPage'
import { PlaceholderPage } from './pages/PlaceholderPage'
import { AnalyticsPage } from './pages/AnalyticsPage'
import { HandoverPage } from './pages/HandoverPage'
import { SlaEscalationPage } from './pages/SlaEscalationPage'
import { TranscriptsPage } from './pages/TranscriptsPage'
import { MenuPage } from './pages/MenuPage'
import { OrdersPage } from './pages/OrdersPage'
import { ConversationsPage } from './pages/ConversationsPage'
import { AdminSettingsPage } from './features/admin/components/AdminSettingsPage'
import { PlatformAdminPage } from './features/platform-admin/components/PlatformAdminPage'
import { PlatformLiveOpsPage } from './features/platform-admin/components/PlatformLiveOpsPage'
import { PasswordResetPage } from './features/platform-admin/components/PasswordResetPage'
import { OrganizationsPage } from './features/platform-admin/components/OrganizationsPage'
import { PlatformPropertiesPage } from './features/platform-admin/components/PlatformPropertiesPage'
import { PlatformTeamsPage } from './features/platform-admin/components/PlatformTeamsPage'
import { PlatformUsersPage } from './features/platform-admin/components/PlatformUsersPage'
import { PlatformDevicesPage } from './features/platform-admin/components/PlatformDevicesPage'
import { PlatformMonitoringPage } from './features/platform-admin/components/PlatformMonitoringPage'
import { PlatformDeviceHealthPage } from './features/platform-admin/components/PlatformDeviceHealthPage'
import { OTAManagementPage } from './features/platform-admin/components/OTAManagementPage'
import { PlatformSupportPage } from './features/platform-admin/components/PlatformSupportPage'
import { TenantSupportPage } from './pages/TenantSupportPage'
import { SupportPage } from './pages/SupportPage'
import { TableOrderPage } from './features/table-order/components/TableOrderPage'
import { useAuthStore } from './features/auth/store/authStore'

// Users who can see feature-preview pages instead of "Coming Soon"
const PREVIEW_EMAILS = new Set(['quickgen@gmail.com', 'support@quickgentech.com'])

function PublicEntryRedirect(): JSX.Element {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''))
  const token =
    searchParams.get('token') ||
    searchParams.get('reset_token') ||
    searchParams.get('t') ||
    hashParams.get('token') ||
    hashParams.get('reset_token') ||
    hashParams.get('t')

  if (token) {
    return <Navigate to={`/reset-password?token=${encodeURIComponent(token)}`} replace />
  }

  return <Navigate to="/login" replace />
}

// The one place that decides where a signed-in user belongs. Login, the role
// guards and a bare "/" all defer to this rather than each naming a page.
function DefaultRedirect(): JSX.Element {
  const user = useAuthStore((s) => s.user)
  const loadingProperties = usePropertyStore((s) => s.loading)
  const initialized = usePropertyStore((s) => s.initialized)

  // Platform admins work above properties, so they are the one role that lands
  // somewhere without picking one.
  if (user?.is_platform_admin) {
    return <Navigate to="/platform-admin/live-ops" replace />
  }
  // Not settled yet — "no property" and "not fetched" look identical otherwise.
  if (loadingProperties || !initialized) {
    return <p className="p-6 text-sm text-gray-400">Loading...</p>
  }
  // Everyone else picks a property first, whatever their role — which pages
  // even exist depends on the vertical, so there is nothing honest to show yet.
  // PropertyLanding owns both halves of that: the picker while nothing is
  // chosen, and the redirect once something is, because it has to hold the
  // redirect back until the selector has finished gliding into the top bar.
  return <PropertyLanding />
}

export default function App(): JSX.Element {
  const restoreSession = useAuthStore((s) => s.restoreSession)
  const user = useAuthStore((s) => s.user)
  const isPreviewUser = user?.email != null && PREVIEW_EMAILS.has(user.email)

  useEffect(() => {
    restoreSession()
  }, [restoreSession])

  return (
    <Routes>
      <Route path="/" element={<PublicEntryRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/password-reset" element={<PublicEntryRedirect />} />
      {/* Where a table QR lands. Public, no layout — must stay above the
          catch-all below, which sends anything unknown to the login screen. */}
      <Route path="/t" element={<ErrorBoundary><TableOrderPage /></ErrorBoundary>} />
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<DefaultRedirect />} />

        {/* No property needed — sitting outside PropertyGuard is the declaration */}
        <Route path="/support-tickets" element={<ErrorBoundary><TenantSupportPage /></ErrorBoundary>} />
        <Route path="/change-password" element={<ChangePasswordPage />} />

        {/* Property-scoped. Which vertical owns a path comes from the sidebar
            nav map, so these stay reachable as long as they are listed there. */}
        <Route element={<PropertyGuard />}>
          <Route path="/live-ops" element={ <ErrorBoundary><LiveOpsPage /></ErrorBoundary> } />
          <Route path="/incidents" element= {<ErrorBoundary><IncidentsPage /></ErrorBoundary> }  />
          <Route path="/handover" element={ <ErrorBoundary><HandoverPage /></ErrorBoundary> } />
          <Route path="/transcripts" element={<ErrorBoundary><TranscriptsPage /></ErrorBoundary>} />
          <Route path="/analytics" element= {<ErrorBoundary><AnalyticsPage /></ErrorBoundary>} />
          <Route path="/sla-escalation" element={isPreviewUser ? <ErrorBoundary><SlaEscalationPage /></ErrorBoundary> : <PlaceholderPage />} />
          <Route path="/conversations" element={<ErrorBoundary><ConversationsPage /></ErrorBoundary>} />
          <Route path="/orders" element={<ErrorBoundary><OrdersPage /></ErrorBoundary>} />
          <Route path="/menu" element={<ErrorBoundary><MenuPage /></ErrorBoundary>} />
          {/* Owned by no vertical — every property type has admin settings */}
          <Route path="/admin" element={<ErrorBoundary><AdminSettingsPage /></ErrorBoundary>} />
        </Route>

        {/* Platform admin routes */}
        <Route path="/platform-admin/live-ops" element={<ErrorBoundary><PlatformLiveOpsPage /></ErrorBoundary>} />
        <Route path="/platform-admin/monitoring" element={<ErrorBoundary><PlatformMonitoringPage /></ErrorBoundary>} />
        <Route path="/platform-admin/admins" element={<ErrorBoundary><PlatformAdminPage /></ErrorBoundary>} />
        <Route path="/platform-admin/password-reset" element={<ErrorBoundary><PasswordResetPage /></ErrorBoundary>} />
        <Route path="/platform-admin/audit-logs" element={<ErrorBoundary><PlatformAdminPage /></ErrorBoundary>} />
        <Route path="/platform-admin/organizations" element={<ErrorBoundary><OrganizationsPage /></ErrorBoundary>} />
        <Route path="/platform-admin/properties" element={<ErrorBoundary><PlatformPropertiesPage /></ErrorBoundary>} />
        <Route path="/platform-admin/teams" element={<ErrorBoundary><PlatformTeamsPage /></ErrorBoundary>} />
        <Route path="/platform-admin/users" element={<ErrorBoundary><PlatformUsersPage /></ErrorBoundary>} />
        <Route path="/platform-admin/devices" element={<ErrorBoundary><PlatformDevicesPage /></ErrorBoundary>} />
        <Route path="/platform-admin/device-health" element={<ErrorBoundary><PlatformDeviceHealthPage /></ErrorBoundary>} />
        <Route path="/platform-admin/ota" element={<ErrorBoundary><OTAManagementPage /></ErrorBoundary>} />
        <Route path="/platform-admin/support" element={<ErrorBoundary><PlatformSupportPage /></ErrorBoundary>} />
        <Route path="/platform-admin/ai-settings" element={<PlaceholderPage />} />
        <Route path="/platform-admin/sla-rules" element={<PlaceholderPage />} />
        <Route path="/unauthorized" element={<PlaceholderPage />} />
      </Route>
      <Route path="*" element={<PublicEntryRedirect />} />
    </Routes>
  )
}
